import { Link } from "react-router-dom";
import { MapPin, Building2, Clock, Banknote, User, Layers } from "lucide-react";
import { Badge } from "./ui/badge";
import { FeaturedBadge, isFeaturedActive } from "./Featured";

// Nuovi tipi di contratto
const contractTypeLabels = {
  tempo_indeterminato: "Tempo indeterminato",
  tempo_determinato: "Tempo determinato",
  apprendistato: "Apprendistato",
  stage: "Stage/Tirocinio",
  contratto_progetto: "A progetto",
  lavoro_temporaneo: "Temporaneo",
  freelance: "Freelance",
  part_time: "Part-time",
  full_time: "Full-time",
  stagionale: "Stagionale",
  a_chiamata: "A chiamata",
  // Legacy
  "full-time": "Tempo pieno",
  "part-time": "Part-time",
  "temporary": "Temporaneo"
};

const contractTypeColors = {
  tempo_indeterminato: "bg-emerald-100 text-emerald-700",
  tempo_determinato: "bg-blue-100 text-blue-700",
  apprendistato: "bg-purple-100 text-purple-700",
  stage: "bg-amber-100 text-amber-700",
  contratto_progetto: "bg-orange-100 text-orange-700",
  lavoro_temporaneo: "bg-amber-100 text-amber-700",
  freelance: "bg-purple-100 text-purple-700",
  part_time: "bg-blue-100 text-blue-700",
  full_time: "bg-emerald-100 text-emerald-700",
  stagionale: "bg-cyan-100 text-cyan-700",
  a_chiamata: "bg-slate-100 text-slate-700",
  // Legacy
  "full-time": "bg-emerald-100 text-emerald-700",
  "part-time": "bg-blue-100 text-blue-700",
  "temporary": "bg-amber-100 text-amber-700"
};

export const JobCard = ({ job, showActions = false, onEdit, onDelete }) => {
  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return null;
    if (job.salary_min && job.salary_max) {
      return `CHF ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    }
    if (job.salary_min) return `Da CHF ${job.salary_min.toLocaleString()}`;
    return `Fino a CHF ${job.salary_max.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-CH", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // Ottieni i tipi di contratto (supporta sia array che singolo valore)
  const getContractTypes = () => {
    if (job.contract_types && Array.isArray(job.contract_types) && job.contract_types.length > 0) {
      return job.contract_types;
    }
    if (job.contract_type) {
      return [job.contract_type];
    }
    return [];
  };

  const contractTypes = getContractTypes();
  const salary = formatSalary();

  return (
    <div 
      className={`job-card card-hover group ${isFeaturedActive(job) ? "ring-2 ring-amber-400 bg-amber-50/40" : ""}`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        {job.image_url ? (
          <img
            src={job.image_url.startsWith('http') ? job.image_url : `${process.env.REACT_APP_BACKEND_URL}${job.image_url}`}
            alt={job.title}
            loading="lazy"
            className="w-full h-40 sm:w-28 sm:h-28 object-cover rounded-lg border border-slate-200 flex-shrink-0"
            data-testid={`job-card-image-${job.id}`}
          />
        ) : (
          <div
            className="w-full h-28 sm:w-28 sm:h-28 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0"
            data-testid={`job-card-logo-${job.id}`}
          >
            <img src="/logo-transparent.png" alt="LavoroTicino.ch" loading="lazy" className="w-3/5 h-3/5 object-contain" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <FeaturedBadge item={job} />
            {job.advertiser_type === "private" && (
              <Badge className="bg-amber-100 text-amber-700">
                <User className="w-3 h-3 mr-1" /> Privato
              </Badge>
            )}
            {contractTypes.slice(0, 2).map((ct) => (
              <Badge key={ct} className={contractTypeColors[ct] || "bg-slate-100 text-slate-700"}>
                {contractTypeLabels[ct] || ct}
              </Badge>
            ))}
            {contractTypes.length > 2 && (
              <Badge variant="outline" className="text-xs">+{contractTypes.length - 2}</Badge>
            )}
            <span className="text-sm text-slate-500">
              {formatDate(job.created_at)}
            </span>
          </div>
          
          <Link 
            to={`/jobs/${job.id}`}
            className="block group-hover:text-[#DA291C] transition-colors"
            data-testid={`job-link-${job.id}`}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-1 line-clamp-1">
              {job.title}
            </h3>
          </Link>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mt-3">
            <div className="flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span>{job.company_name}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-slate-400" />
              <span>{job.location}</span>
            </div>
            {job.sector && (
              <div className="flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-slate-400" />
                <span>{job.sector}</span>
              </div>
            )}
            {salary && (
              <div className="flex items-center gap-1.5">
                <Banknote className="w-4 h-4 text-slate-400" />
                <span>{salary}</span>
              </div>
            )}
          </div>
          
          <p className="text-slate-600 mt-3 line-clamp-2 text-sm">
            {job.description}
          </p>
        </div>

        <div className="flex sm:flex-col gap-2">
          <Link 
            to={`/jobs/${job.id}`}
            className="inline-flex items-center justify-center px-4 py-2 bg-[#004E64] hover:bg-[#003B4D] text-white text-sm font-medium rounded-md transition-colors"
            data-testid={`job-view-btn-${job.id}`}
          >
            Dettagli
          </Link>
          
          {showActions && (
            <div className="flex gap-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(job)}
                  className="px-3 py-2 text-sm text-slate-600 hover:text-[#004E64] hover:bg-slate-50 rounded-md transition-colors"
                  data-testid={`job-edit-btn-${job.id}`}
                >
                  Modifica
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(job.id)}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  data-testid={`job-delete-btn-${job.id}`}
                >
                  Elimina
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
